import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Plus, Users, Phone, Mail, MapPin, Trash2, Pencil } from 'lucide-react';

const emptyForm = { name: '', phone: '', email: '', address: '', site_address: '' };

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try { const { data } = await api.get('/customers'); setCustomers(data); } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditingId(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({ name: c.name || '', phone: c.phone || '', email: c.email || '', address: c.address || '', site_address: c.site_address || '' });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/customers/${editingId}`, form);
      } else {
        await api.post('/customers', form);
      }
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm);
      load();
    } catch {}
  };

  const deleteCustomer = async (id) => {
    if (!window.confirm('Delete this customer?')) return;
    await api.delete(`/customers/${id}`);
    load();
  };

  const filtered = customers.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.phone?.includes(search) ||
    c.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight text-slate-900">Customers</h1>
          <p className="text-slate-500 text-sm mt-1">{customers.length} customer{customers.length !== 1 ? 's' : ''}</p>
        </div>
        <Dialog open={showForm} onOpenChange={(o) => { setShowForm(o); if (!o) { setEditingId(null); setForm(emptyForm); } }}>
          <DialogTrigger asChild>
            <Button onClick={openAdd} className="bg-[#ed8936] hover:bg-[#dd6b20] text-white" data-testid="add-customer-btn">
              <Plus size={16} className="mr-1" /> Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Outfit']">{editingId ? 'Edit Customer' : 'Add New Customer'}</DialogTitle>
              <DialogDescription>{editingId ? 'Update customer details.' : 'Enter customer details below.'}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="customer-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="customer-phone-input" /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="customer-email-input" /></div>
              </div>
              <div><Label>Address</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></div>
              <div><Label>Site Address</Label><Input value={form.site_address} onChange={e => setForm({...form, site_address: e.target.value})} /></div>
              <Button type="submit" className="w-full bg-[#ed8936] hover:bg-[#dd6b20] text-white" data-testid="save-customer-btn">
                {editingId ? 'Update Customer' : 'Save Customer'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Input
        placeholder="Search customers by name, phone, or email..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="max-w-md"
        data-testid="customer-search"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(c => (
          <Card key={c.id} className="border border-slate-200 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200" data-testid={`customer-card-${c.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#0B2545] flex items-center justify-center text-white font-['Outfit'] font-semibold text-sm">
                    {c.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <h3 className="font-['Outfit'] font-medium text-slate-900">{c.name}</h3>
                    <Badge variant="secondary" className="text-xs mt-0.5">{c.quotation_count || 0} quotations</Badge>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => openEdit(c)}
                    className="text-slate-400 hover:text-[#0B2545] p-1 rounded hover:bg-slate-100"
                    title="Edit customer"
                    data-testid={`edit-customer-${c.id}`}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => deleteCustomer(c.id)}
                    className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50"
                    title="Delete customer"
                    data-testid={`delete-customer-${c.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="mt-3 space-y-1.5 text-sm text-slate-500">
                {c.phone && <div className="flex items-center gap-2"><Phone size={13} />{c.phone}</div>}
                {c.email && <div className="flex items-center gap-2"><Mail size={13} />{c.email}</div>}
                {c.address && <div className="flex items-center gap-2"><MapPin size={13} />{c.address}</div>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          <Users size={48} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">{search ? 'No matching customers' : 'No customers yet. Add your first customer.'}</p>
        </div>
      )}
    </div>
  );
}
